import { useEffect, useState, useRef, type PointerEvent } from "react";
import { useValue } from "@tldraw/state-react";
import type { TLShapeId } from "@tldraw/tlschema";
import type { Trace } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { editableTarget } from "./tracing";
import {
  areaBounds,
  rectanglePoints,
  type AreaPoint,
} from "../shared/trace-area";
import { copyAreaToPaper } from "./trace-area-copy";
import {
  copyObjectsToPaper,
  insidePaper,
  objectUnderPoint,
  objectsUnderPaper,
} from "./tracing-sheet";

type Mode = "rectangle" | "polygon" | "objects";
export function TracePickOverlay({
  model,
  traceId,
}: {
  model: WorkspaceModel;
  traceId: TLShapeId;
}) {
  const { editor } = model;
  const trace = useValue(
    "paper for picking",
    () => editor.getShape<Trace>(traceId),
    [editor, traceId],
  );
  const allowed = useValue(
    "editable paper",
    () => editableTarget(editor, editor.getShape(traceId)),
    [editor, traceId],
  );
  const [mode, setMode] = useState<Mode>("rectangle");
  const [selected, setSelected] = useState<TLShapeId[]>([]);
  // Selection vertices use paper coordinates so rectangles follow rotated paper too.
  const [points, setPoints] = useState<AreaPoint[]>([]);
  const [cursor, setCursor] = useState<AreaPoint | null>(null);
  const [finished, setFinished] = useState(false);
  const [dragging, setDragging] = useState(false);
  const start = useRef<AreaPoint | null>(null);
  const abort = useRef(new AbortController());
  const [copying, setCopying] = useState(false);
  const inFlight = useRef(false);
  const [message, setMessage] = useState("");
  const reset = () => {
    setPoints([]);
    setCursor(null);
    setFinished(false);
    setDragging(false);
    setSelected([]);
    start.current = null;
    setMessage("");
    editor.setHintingShapes([]);
  };
  const close = (resume = true) => {
    abort.current.abort();
    model.setPickingTrace(null);
    editor.setHintingShapes([]);
    if (resume && editableTarget(editor, editor.getShape(traceId)))
      model.annotation.begin(traceId);
    else editor.setCurrentTool("select");
  };
  useEffect(() => {
    abort.current = new AbortController();
    return () => {
      abort.current.abort();
      editor.setHintingShapes([]);
    };
  }, [editor, traceId]);
  useEffect(() => {
    if (!allowed) close(false);
  }, [allowed]);
  let valid = false;
  try {
    areaBounds(points);
    valid = true;
  } catch {
    /* incomplete selection */
  }
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement)?.matches("input,textarea,select"))
        return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (points.length && !copying) reset();
        else close();
      }
      if (copying || mode !== "polygon") return;
      if (event.key === "Backspace") {
        event.preventDefault();
        setPoints((p) => p.slice(0, -1));
        setFinished(false);
      }
      if (event.key === "Enter" && valid) {
        event.preventDefault();
        setFinished(true);
        setCursor(null);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [points, mode, copying, valid]);
  if (!trace || trace.type !== "trace") return null;
  const candidates = objectsUnderPaper(editor, trace);
  const toggle = (id: TLShapeId) =>
    setSelected((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  const pagePoint = (e: PointerEvent) =>
    editor.screenToPage({ x: e.clientX, y: e.clientY });
  const localPoint = (e: PointerEvent) => {
    const p = editor.getPointInShapeSpace(trace, pagePoint(e));
    return {
      x: Math.max(0, Math.min(trace.props.w, p.x)),
      y: Math.max(0, Math.min(trace.props.h, p.y)),
    };
  };
  const screenPoint = (p: AreaPoint) =>
    editor.pageToScreen(editor.getShapePageTransform(trace).applyToPoint(p));
  const copy = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setCopying(true);
    editor.setHintingShapes([]);
    try {
      if (mode === "objects") {
        copyObjectsToPaper(editor, trace.id, selected);
      } else {
        await copyAreaToPaper(
          editor,
          model.room,
          trace.id,
          points.map((p) =>
            editor.getShapePageTransform(trace).applyToPoint(p),
          ),
          abort.current.signal,
        );
      }
      if (!abort.current.signal.aborted) close();
    } catch (error) {
      if (!abort.current.signal.aborted) setMessage((error as Error).message);
    } finally {
      inFlight.current = false;
      setCopying(false);
    }
  };
  const pathPoints = [
    ...points,
    ...(mode === "polygon" && !finished && cursor ? [cursor] : []),
  ].map(screenPoint);
  const pointString = pathPoints.map((p) => `${p.x},${p.y}`).join(" ");
  const ready =
    mode === "objects"
      ? selected.length > 0
      : valid && !dragging && (mode === "rectangle" || finished);
  const anchors =
    mode === "objects"
      ? selected.flatMap((id) => {
          const b = editor.getShapePageBounds(id);
          return b
            ? [
                editor.pageToScreen(b),
                editor.pageToScreen({ x: b.maxX, y: b.maxY }),
              ]
            : [];
        })
      : points.map(screenPoint);
  const minX = Math.min(...anchors.map((p) => p.x)),
    maxX = Math.max(...anchors.map((p) => p.x));
  const minY = Math.min(...anchors.map((p) => p.y)),
    maxY = Math.max(...anchors.map((p) => p.y));
  const actionPosition = {
    left: Math.max(
      12,
      Math.min(window.innerWidth - 264, (minX + maxX) / 2 - 126),
    ),
    top: Math.max(
      88,
      Math.min(
        window.innerHeight - 72,
        maxY + 12 <= window.innerHeight - 72 ? maxY + 12 : minY - 56,
      ),
    ),
  };
  return (
    <>
      <div
        className="trace-pick-surface"
        aria-label="Pick through tracing paper"
        onPointerDown={(e) => {
          if (e.button !== 0 || copying) return;
          if (!insidePaper(editor, trace, pagePoint(e))) {
            setMessage(
              "Choose an area inside the tracing sheet, or move the paper over the drawing first.",
            );
            return;
          }
          setMessage("");
          if (mode === "objects") {
            const hit = objectUnderPoint(editor, trace, pagePoint(e));
            if (hit) toggle(hit.id);
            return;
          }
          const p = localPoint(e);
          if (mode === "polygon") {
            if (finished) {
              setPoints([p]);
              setFinished(false);
            } else if (
              points.length >= 3 &&
              Math.hypot(
                (p.x - points[0].x) * model.zoom,
                (p.y - points[0].y) * model.zoom,
              ) < 12
            ) {
              setFinished(true);
              setCursor(null);
            } else if (points.length < 128) setPoints([...points, p]);
            else setMessage("Use up to 128 corners, then copy the area.");
          } else {
            start.current = p;
            setPoints(rectanglePoints(p, p));
            setDragging(true);
            e.currentTarget.setPointerCapture(e.pointerId);
          }
        }}
        onPointerMove={(e) => {
          if (copying) return;
          if (mode === "rectangle" && dragging && start.current)
            setPoints(rectanglePoints(start.current, localPoint(e)));
          else if (mode === "polygon" && !finished) setCursor(localPoint(e));
          else if (mode === "objects") {
            const hit = objectUnderPoint(editor, trace, pagePoint(e));
            editor.setHintingShapes(hit ? [hit.id] : []);
          }
        }}
        onPointerUp={() => {
          setDragging(false);
          start.current = null;
        }}
        onPointerCancel={() => {
          if (mode === "rectangle") reset();
        }}
      >
        <svg className="trace-pick-highlights">
          {mode === "objects" ? (
            selected.map((id) => {
              const b = editor.getShapePageBounds(id);
              if (!b) return null;
              const p = editor.pageToScreen(b);
              return (
                <rect
                  key={id}
                  x={p.x}
                  y={p.y}
                  width={b.w * model.zoom}
                  height={b.h * model.zoom}
                />
              );
            })
          ) : (
            <>
              <polygon points={pointString} fillRule="evenodd" />
              {mode === "polygon" &&
                points.map((p, i) => {
                  const q = screenPoint(p);
                  return (
                    <circle key={i} cx={q.x} cy={q.y} r={i === 0 ? 6 : 3} />
                  );
                })}
            </>
          )}
        </svg>
      </div>
      {ready && (
        <div
          className="trace-selection-actions floating-surface"
          style={actionPosition}
          role="dialog"
          aria-label="Selected area actions"
        >
          <button className="primary" disabled={copying} onClick={copy}>
            {copying
              ? "Copying…"
              : mode === "objects"
                ? `Copy ${selected.length} item${selected.length === 1 ? "" : "s"}`
                : "Copy as image"}
          </button>
          <button onClick={() => close()}>Cancel</button>
          {message && <small role="status">{message}</small>}
        </div>
      )}
      {!ready && (
        <div
          className="trace-pick-controls floating-surface"
          role="dialog"
          aria-label="Copy from underneath"
        >
          <strong>Copy underneath</strong>
          <div className="format-buttons">
            {(
              [
                ["rectangle", "Rectangle"],
                ["polygon", "Polygon"],
                ["objects", "Objects"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                disabled={copying}
                aria-pressed={mode === value}
                onClick={() => {
                  reset();
                  setMode(value);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <p>
            {mode === "rectangle"
              ? "Drag an area to copy as one image."
              : mode === "polygon"
                ? "Click corners; Enter finishes the image outline."
                : "Select whole objects to keep editable copies."}
          </p>
          {mode === "polygon" && points.length > 0 && (
            <div className="format-buttons">
              <button
                disabled={copying || !valid || finished}
                onClick={() => {
                  setFinished(true);
                  setCursor(null);
                }}
              >
                Finish selection
              </button>
              <button
                disabled={copying}
                onClick={() => {
                  setPoints((p) => p.slice(0, -1));
                  setFinished(false);
                }}
              >
                Undo corner
              </button>
            </div>
          )}
          {mode === "objects" && (
            <details>
              <summary>Items underneath ({candidates.length})</summary>
              <div className="trace-pick-list">
                {candidates.map((s, i) => (
                  <label key={s.id}>
                    <input
                      type="checkbox"
                      disabled={copying}
                      checked={selected.includes(s.id)}
                      onChange={() => toggle(s.id)}
                    />
                    {s.type === "image"
                      ? s.props.altText || "Image"
                      : s.type === "text"
                        ? model.textOf(s.props.richText).slice(0, 60) || "Text"
                        : `${s.type} ${i + 1}`}
                  </label>
                ))}
              </div>
            </details>
          )}
          {(points.length > 0 || selected.length > 0) && (
            <button disabled={copying} onClick={reset}>
              Clear selection
            </button>
          )}
          {message && <small role="status">{message}</small>}
          <button onClick={() => close()}>Cancel</button>
        </div>
      )}
    </>
  );
}
